import {computeLine, round2, taxBreakdown} from "./posMath";

// Ticket de caisse de la caisse web — même contenu que le ticket client de la
// caisse desktop (electron-pos services/receiptHelper.js buildReceiptMessage) :
// en-tête commerce, date / vendeur / client, articles, remises, TVA par taux,
// paiements, monnaie rendue, pied de page. Ici en HTML 80 mm imprimé par le
// navigateur (pas d'ESC/POS : aucune imprimante réseau depuis le web).

export const RECEIPT_FOOTER = "Merci pour votre visite !";

// Instantané figé au moment de l'encaissement : le ticket reste réimprimable
// (historique de session) même après que le panier a été vidé.
export const buildReceiptSnapshot = ({sale, totals, payments, methods = [], change = 0, employeeName = "", date = Date.now()}) => ({
    saleId: sale.id,
    date,
    employeeName,
    clientName: sale.client?.name || "",
    note: sale.note || "",
    lines: sale.lines.map((l) => {
        const r = computeLine(l);
        return {
            name: l.name,
            quantity: Number(l.quantity) || 0,
            price: Number(l.price) || 0,
            gross: round2(r.gross),
            discount: round2(r.discount),
            total: round2(r.total),
            note: l.isFree ? "" : (l.note || ""),
        };
    }),
    totals: {
        gross: round2(totals.gross),
        discount: round2(totals.discount),
        total: round2(totals.total),
        tax: round2(totals.tax),
        count: totals.count,
    },
    tva: taxBreakdown(sale.lines).map(({rate, amount}) => ({rate, amount: round2(amount)})),
    payments: payments.map((p) => ({
        name: methods.find((m) => m.id === p.paymentId)?.name || "Paiement",
        amount: round2(p.amount),
    })),
    change: round2(change),
});

// Référence courte lisible : 8 premiers caractères de l'UUID de la vente.
export const ticketRef = (saleId) => String(saleId || "").replace(/-/g, "").slice(0, 8).toUpperCase();

const esc = (v) => String(v ?? "")
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;").replace(/'/g, "&#39;");

const amount = (v) => (Number(v) || 0).toFixed(2);

const formatDate = (ms) => {
    const d = new Date(ms);
    const pad = (n) => String(n).padStart(2, "0");
    return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

// store : {name, address, phone, ice, idFiscal, footer}
export const storeInfoFromAccount = (account = {}) => ({
    name: account?.name || "Mon Commerce",
    address: account?.address || account?.adresse || "",
    phone: account?.phone || "",
    ice: account?.ice || "",
    idFiscal: account?.idFiscal || "",
    footer: RECEIPT_FOOTER,
});

const row = (label, value, cls = "") =>
    `<div class="row ${cls}"><span>${label}</span><span>${value}</span></div>`;

export const buildReceiptHtml = (r, store = storeInfoFromAccount()) => {
    const head = [
        `<div class="title">${esc(store.name)}</div>`,
        store.address && `<div>${esc(store.address)}</div>`,
        store.phone && `<div>Tél : ${esc(store.phone)}</div>`,
        store.ice && `<div>ICE : ${esc(store.ice)}</div>`,
        store.idFiscal && `<div>IF : ${esc(store.idFiscal)}</div>`,
    ].filter(Boolean).join("");

    const meta = [
        row("Ticket", `#${esc(ticketRef(r.saleId))}`),
        row("Date", esc(formatDate(r.date))),
        r.employeeName && row("Vendeur", esc(r.employeeName)),
        r.clientName && row("Client", esc(r.clientName)),
    ].filter(Boolean).join("");

    const items = r.lines.map((l) => `
        <div class="item">
            <div class="row"><span class="name">${esc(l.name)}</span><span>${amount(l.gross)}</span></div>
            <div class="sub">${l.quantity} × ${amount(l.price)}</div>
            ${l.discount > 0 ? row("&nbsp;&nbsp;Remise", `−${amount(l.discount)}`, "sub") : ""}
            ${l.note ? `<div class="sub">&nbsp;&nbsp;${esc(l.note)}</div>` : ""}
        </div>`).join("");

    const totals = [
        r.totals.discount > 0 && row("Sous-total", amount(r.totals.gross)),
        r.totals.discount > 0 && row("Remise", `−${amount(r.totals.discount)}`),
        row("TOTAL TTC", `${amount(r.totals.total)} DH`, "total"),
        ...r.tva.map((t) => row(`dont TVA ${t.rate} %`, amount(t.amount), "sub")),
    ].filter(Boolean).join("");

    const pay = [
        ...r.payments.map((p) => row(esc(p.name), amount(p.amount))),
        r.change > 0 && row("Monnaie rendue", amount(r.change), "bold"),
    ].filter(Boolean).join("");

    return `<!doctype html><html lang="fr"><head><meta charset="utf-8"><title>Ticket ${esc(ticketRef(r.saleId))}</title>
<style>
@page { size: 80mm auto; margin: 0; }
* { box-sizing: border-box; }
body { margin: 0; padding: 4mm 3mm; width: 80mm; font: 12px/1.35 "Courier New", ui-monospace, monospace; color: #000; background: #fff; }
.center { text-align: center; }
.title { font-size: 18px; font-weight: 700; margin-bottom: 2px; }
.row { display: flex; justify-content: space-between; gap: 8px; }
.row span:last-child { white-space: nowrap; }
.name { font-weight: 700; }
.sub { font-size: 11px; }
.bold { font-weight: 700; }
.total { font-size: 16px; font-weight: 700; margin: 3px 0; }
.item { margin-bottom: 3px; }
hr { border: 0; border-top: 1px dashed #000; margin: 6px 0; }
</style></head><body>
<div class="center">${head}</div>
<hr>${meta}<hr>
${items}
<hr>${totals}
${pay ? `<hr>${pay}` : ""}
${r.note ? `<hr><div class="sub">Note : ${esc(r.note)}</div>` : ""}
<hr><div class="center">${esc(store.footer)}</div>
</body></html>`;
};

// Impression via une iframe cachée : pas de fenêtre pop-up (bloquée par les
// navigateurs) et la page de caisse reste en place.
export const printHtml = (html) => {
    const frame = document.createElement("iframe");
    frame.setAttribute("aria-hidden", "true");
    Object.assign(frame.style, {position: "fixed", right: "0", bottom: "0", width: "0", height: "0", border: "0"});
    document.body.appendChild(frame);
    const doc = frame.contentWindow?.document;
    if (!doc) {
        frame.remove();
        return false;
    }
    doc.open();
    doc.write(html);
    doc.close();
    const cleanup = () => setTimeout(() => frame.remove(), 500);
    frame.contentWindow.onafterprint = cleanup;
    setTimeout(() => {
        try {
            frame.contentWindow.focus();
            frame.contentWindow.print();
        } finally {
            setTimeout(() => frame.parentNode && frame.remove(), 60000);
        }
    }, 50);
    return true;
};

export const printReceipt = (snapshot, store) => printHtml(buildReceiptHtml(snapshot, store));
